import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { 
  Layers,
  Users,
  DollarSign,
  TrendingUp,
  CheckCircle,
  Award,
  BarChart3,
  Target,
  Zap,
  Shield,
  Clock,
  Building2,
  Phone,
  Calendar,
  Star,
  GitBranch,
  FileText
} from 'lucide-react';

const RCMMultispecialtyPractices = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>RCM for Multispecialty Practices | Enterprise | MDeRCM</title>
        <meta 
          name="description" 
          content="Comprehensive RCM for multispecialty practices. Integrated billing across all specialties, unified reporting & AI denial management." 
        />
        <meta 
          name="keywords" 
          content="revenue cycle management for multispecialty practices, multispecialty billing services, enterprise RCM solutions, large practice billing, multi-location billing, integrated RCM, specialty coordination" 
        />
        <meta property="og:title" content="RCM for Multispecialty Practices | Enterprise | MDeRCM" />
        <meta property="og:description" content="Comprehensive RCM for multispecialty practices. Integrated billing across all specialties." />
        <link rel="canonical" href="https://mdercm.com/revenue-cycle-management-multispecialty-practices" />
      </Helmet>

      <main style={{ minHeight: '100vh', backgroundColor: '#ffffff', fontFamily: "'Poppins', sans-serif" }}>
        
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
          color: '#ffffff',
          padding: isMobile ? '60px 20px' : '80px 40px'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ maxWidth: '56rem', margin: '0 auto', textAlign: 'center' }}>
              <div style={{
                display: 'inline-block',
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '8px 20px',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '600',
                marginBottom: '20px'
              }}>
                🏢 Enterprise-Grade RCM Solutions
              </div>
              
              <h1 style={{
                fontSize: isMobile ? '2rem' : '3rem',
                fontWeight: 'bold',
                marginBottom: '24px',
                lineHeight: '1.2'
              }}>
                Revenue Cycle Management for Multispecialty Practices
              </h1>
              
              <p style={{
                fontSize: isMobile ? '1.125rem' : '1.25rem',
                marginBottom: '32px',
                opacity: 0.95,
                lineHeight: '1.75'
              }}>
                Transform your complex, multi-specialty practice into a unified revenue powerhouse. Our integrated RCM platform coordinates billing across all specialties, maximizes collections by 25-35%, and provides enterprise-level analytics—all while reducing administrative overhead by 30-40%.
              </p>

              {/* Key Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                gap: '20px',
                marginTop: '40px'
              }}>
                {[
                  { icon: DollarSign, value: '25-35%', label: 'Revenue Increase' },
                  { icon: TrendingUp, value: '30-40%', label: 'Cost Reduction' },
                  { icon: Award, value: '99%+', label: 'Clean Claim Rate' },
                  { icon: Clock, value: '20-30%', label: 'Faster Payments' }
                ].map((stat, idx) => (
                  <div key={idx} style={{
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    padding: '20px',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <stat.icon size={32} style={{ marginBottom: '12px' }} />
                    <div style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '4px' }}>
                      {stat.value}
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: '16px',
                justifyContent: 'center',
                marginTop: '40px'
              }}>
                <Link to="/free-trial" style={{
                  textDecoration: 'none',
                  backgroundColor: '#F26522',
                  color: '#ffffff',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-block',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d3541c'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F26522'}
                >
                  Get Enterprise Assessment →
                </Link>
                
                <a href="tel:+15103566069" style={{
                  textDecoration: 'none',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: '#ffffff',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-block',
                  border: '2px solid rgba(255,255,255,0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                >
                  📞 Call: +1 (510) 356-6069
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151',
              marginBottom: '24px'
            }}>
              Managing revenue cycle for a multispecialty practice isn't just complex—it's exponentially complicated. Each specialty has unique coding requirements, different payer contracts, specialty-specific compliance needs, and distinct workflow patterns. When you multiply these challenges across 15, 20, or 30+ providers, the result is often chaos, confusion, and significant revenue leakage.
            </p>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151',
              marginBottom: '24px'
            }}>
              <strong style={{ color: '#4f46e5' }}>Revenue cycle management for multispecialty practices</strong> demands more than standard billing services. You need integrated systems that coordinate across specialties, unified reporting that provides both practice-wide and specialty-specific insights, and expert teams that understand the nuances of each specialty you serve.
            </p>
            <p style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#374151'
            }}>
              Our enterprise-grade RCM solution is specifically architected for large, complex practices. We provide specialty-specific coding teams, integrated technology platforms, cross-specialty coordination, and strategic analytics that transform your multispecialty practice from a collection of independent departments into a cohesive, high-performing revenue organization.
            </p>
          </div>
        </section>

        {/* Unique Challenges */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#eef2ff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              8 Critical Challenges Multispecialty Practices Face
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '48px',
              maxWidth: '48rem',
              margin: '0 auto 48px auto'
            }}>
              Complexity that costs millions in lost revenue annually
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: '24px'
            }}>
              {[
                {
                  title: '1. Siloed Specialty Operations',
                  problem: 'Each specialty operates as its own island with separate workflows, different billing staff, inconsistent processes, and no standardization. This creates inefficiency, duplication of effort, and makes practice-wide optimization nearly impossible.',
                  impact: 'Administrative costs 40-50% higher than integrated practices. Lost opportunities for process improvement and economies of scale cost $100K-$300K annually.',
                  icon: GitBranch,
                  color: '#4f46e5'
                },
                {
                  title: '2. Specialty-Specific Coding Complexity',
                  problem: 'Multispecialty practices need expertise across cardiology, orthopedics, dermatology, internal medicine, pediatrics, and more. Each specialty has thousands of codes, unique modifiers, specialty-specific documentation requirements, and constantly changing guidelines.',
                  impact: 'Coding errors cost 8-12% of potential revenue. For a $5M practice, that\'s $400K-$600K in preventable losses annually.',
                  icon: Layers,
                  color: '#6366f1'
                },
                {
                  title: '3. Disparate Payer Contracts',
                  problem: 'Different specialties have different payer contracts, varying reimbursement rates, specialty-specific prior authorization requirements, and unique fee schedules. Managing this complexity without integrated systems creates confusion and missed revenue.',
                  impact: 'Undercoding due to confusion costs 5-8% of revenue. Contract misapplication and missed negotiation opportunities cost $150K-$400K annually.',
                  icon: FileText,
                  color: '#818cf8'
                },
                {
                  title: '4. Cross-Specialty Coordination Failures',
                  problem: 'Patients seeing multiple specialists create coordination challenges: shared visits, consultation billing, diagnostic study coordination, and treatment planning across specialties. Poor coordination leads to missed charges, duplicate billing, and compliance risks.',
                  impact: 'Missed charges from coordination failures: 3-5% of revenue. Duplicate billing cleanup and compliance issues cost $80K-$200K annually.',
                  icon: Users,
                  color: '#4f46e5'
                },
                {
                  title: '5. Inconsistent Reporting & Analytics',
                  problem: 'Without unified systems, getting comprehensive financial data is impossible. Each specialty has different metrics, incompatible systems, and varied reporting formats. Leadership can\'t see the full financial picture or make data-driven decisions.',
                  impact: 'Strategic opportunities missed due to poor visibility cost $200K-$500K annually. Inability to identify underperforming areas compounds losses over time.',
                  icon: BarChart3,
                  color: '#6366f1'
                },
                {
                  title: '6. Staffing & Training Complexity',
                  problem: 'Multispecialty practices need billing staff trained across multiple specialties, creating recruiting challenges, longer training periods, higher turnover rates, and costly knowledge loss when staff leave.',
                  impact: 'Training costs per specialty: $15K-$25K. Turnover costs: $30K-$50K per employee. Total annual staffing cost premium: $150K-$300K.',
                  icon: Target,
                  color: '#818cf8'
                },
                {
                  title: '7. Technology Integration Nightmares',
                  problem: 'Multiple EHRs or PM systems across specialties create integration headaches, data silos, inefficient workflows, and enormous IT overhead. Many practices spend $100K-$300K annually on technology that doesn\'t talk to itself.',
                  impact: 'Technology costs 30-40% higher than integrated practices. Inefficiency from poor integration costs $200K-$400K in lost productivity annually.',
                  icon: Zap,
                  color: '#4f46e5'
                },
                {
                  title: '8. Compliance & Risk Management',
                  problem: 'Multispecialty practices face compliance complexity across multiple specialties, each with unique regulations, audit risks, and documentation requirements. One compliance failure can jeopardize the entire practice.',
                  impact: 'Audit exposure from inconsistent compliance: high risk. Single significant audit finding can cost $500K-$2M+ in penalties, back payments, and legal fees.',
                  icon: Shield,
                  color: '#6366f1'
                }
              ].map((challenge, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  padding: '24px',
                  borderRadius: '12px',
                  border: `3px solid ${challenge.color}`,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(79, 70, 229, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <challenge.icon size={28} style={{ color: challenge.color }} />
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      color: challenge.color,
                      margin: 0
                    }}>
                      {challenge.title}
                    </h3>
                  </div>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '8px'
                    }}>
                      THE CHALLENGE:
                    </div>
                    <p style={{
                      fontSize: '0.9375rem',
                      lineHeight: '1.6',
                      color: '#4b5563',
                      margin: 0
                    }}>
                      {challenge.problem}
                    </p>
                  </div>

                  <div style={{
                    backgroundColor: '#eef2ff',
                    padding: '12px',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${challenge.color}`
                  }}>
                    <div style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: challenge.color,
                      marginBottom: '6px'
                    }}>
                      💰 FINANCIAL IMPACT:
                    </div>
                    <p style={{
                      fontSize: '0.9375rem',
                      lineHeight: '1.6',
                      color: '#374151',
                      margin: 0
                    }}>
                      {challenge.impact}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Integrated Solution */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              Integrated RCM Solution for Multispecialty Success
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '48px',
              maxWidth: '48rem',
              margin: '0 auto 48px auto'
            }}>
              Enterprise platform that unifies billing across all specialties
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '24px'
            }}>
              {[
                {
                  icon: Layers,
                  title: 'Specialty-Specific Expertise',
                  items: [
                    'Dedicated coding teams per specialty',
                    'Certified coders (CPC, CCS, specialty-specific)',
                    'Deep knowledge across all major specialties',
                    'Continuous education on specialty changes',
                    'Specialty-specific best practices'
                  ],
                  benefit: 'Optimize coding accuracy 98%+'
                },
                {
                  icon: GitBranch,
                  title: 'Unified Technology Platform',
                  items: [
                    'Single integrated billing system',
                    'All-specialty EHR integration',
                    'Cross-specialty workflow coordination',
                    'Unified patient financial management',
                    'Enterprise-grade security & compliance'
                  ],
                  benefit: 'Reduce tech costs 30-40%'
                },
                {
                  icon: BarChart3,
                  title: 'Comprehensive Analytics',
                  items: [
                    'Practice-wide financial dashboards',
                    'Specialty-specific performance metrics',
                    'Provider-level productivity tracking',
                    'Payer mix and reimbursement analysis',
                    'Predictive financial modeling'
                  ],
                  benefit: 'Data-driven decision making'
                },
                {
                  icon: Users,
                  title: 'Cross-Specialty Coordination',
                  items: [
                    'Integrated patient billing management',
                    'Consultation and shared visit coding',
                    'Coordinated diagnostic study billing',
                    'Multi-specialty treatment planning support',
                    'Unified patient communication'
                  ],
                  benefit: 'Capture 100% of billable services'
                },
                {
                  icon: Target,
                  title: 'Denial Management',
                  items: [
                    'Specialty-specific denial prevention',
                    'Coordinated authorization management',
                    'Proactive eligibility verification',
                    'Expert appeals by specialty',
                    'Root cause analysis and correction'
                  ],
                  benefit: 'Reduce denials to 2-3%'
                },
                {
                  icon: Shield,
                  title: 'Compliance & Risk Management',
                  items: [
                    'Multi-specialty compliance monitoring',
                    'Automated audit risk detection',
                    'Specialty-specific documentation review',
                    'Regulatory update management',
                    'Comprehensive audit defense'
                  ],
                  benefit: 'Minimize audit risk & penalties'
                },
                {
                  icon: Clock,
                  title: 'Accelerated Collections',
                  items: [
                    '24-hour claim submission all specialties',
                    'Specialty-optimized AR workflows',
                    'Coordinated patient payment plans',
                    'Strategic follow-up prioritization',
                    'Reduce days in AR 25-35%'
                  ],
                  benefit: 'Improve cash flow $500K-$1M+'
                },
                {
                  icon: Award,
                  title: 'Contract Optimization',
                  items: [
                    'Specialty-specific rate analysis',
                    'Multi-specialty contract negotiation',
                    'Reimbursement benchmarking',
                    'Fee schedule optimization',
                    'Value-based contract management'
                  ],
                  benefit: 'Increase rates 5-10%'
                },
                {
                  icon: Building2,
                  title: 'Multi-Location Support',
                  items: [
                    'Centralized billing for all locations',
                    'Location-specific reporting available',
                    'Coordinated patient financial experience',
                    'Unified payer credentialing',
                    'Consistent processes across sites'
                  ],
                  benefit: 'Seamless multi-site operations'
                }
              ].map((solution, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#eef2ff',
                  padding: '28px',
                  borderRadius: '12px',
                  border: '2px solid #c7d2fe',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#4f46e5';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(79, 70, 229, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#c7d2fe';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                >
                  <solution.icon size={40} style={{ color: '#4f46e5', marginBottom: '16px' }} />
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    marginBottom: '16px',
                    color: '#111827'
                  }}>
                    {solution.title}
                  </h3>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 16px 0'
                  }}>
                    {solution.items.map((item, itemIdx) => (
                      <li key={itemIdx} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        marginBottom: '10px',
                        fontSize: '0.9375rem',
                        color: '#4b5563'
                      }}>
                        <CheckCircle size={18} style={{ color: '#4f46e5', flexShrink: 0, marginTop: '2px' }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div style={{
                    backgroundColor: '#4f46e5',
                    color: '#ffffff',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    💰 {solution.benefit}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ROI Example */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ecfdf5' }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '16px',
              color: '#111827'
            }}>
              Financial Transformation Example
            </h2>
            <p style={{
              fontSize: '1.125rem',
              textAlign: 'center',
              color: '#059669',
              marginBottom: '40px',
              fontWeight: '600'
            }}>
              25-provider multispecialty practice collecting $8M annually
            </p>

            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: isMobile ? '32px 24px' : '48px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
              marginBottom: '40px'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '32px',
                color: '#4f46e5',
                textAlign: 'center'
              }}>
                Annual Financial Impact Breakdown
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '24px',
                marginBottom: '32px'
              }}>
                {/* Revenue Increases */}
                <div style={{
                  backgroundColor: '#ecfdf5',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '2px solid #6ee7b7'
                }}>
                  <h4 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#059669',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <TrendingUp size={24} />
                    Revenue Increases
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { item: 'Improved coding accuracy (4%)', amount: '$320,000' },
                      { item: 'Better charge capture (3%)', amount: '$240,000' },
                      { item: 'Reduced denials (3%)', amount: '$240,000' },
                      { item: 'Contract optimization (2%)', amount: '$160,000' },
                      { item: 'Faster collections benefit', amount: '$80,000' }
                    ].map((row, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '12px',
                        backgroundColor: '#ffffff',
                        borderRadius: '6px',
                        fontSize: '0.9375rem'
                      }}>
                        <span style={{ color: '#374151' }}>{row.item}</span>
                        <span style={{ fontWeight: '600', color: '#059669' }}>{row.amount}</span>
                      </div>
                    ))}
                    <div style={{
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: '2px solid #6ee7b7',
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '1.125rem',
                      fontWeight: '700'
                    }}>
                      <span style={{ color: '#059669' }}>Total Revenue Gain:</span>
                      <span style={{ color: '#059669' }}>$1,040,000</span>
                    </div>
                  </div>
                </div>

                {/* Cost Savings */}
                <div style={{
                  backgroundColor: '#eff6ff',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '2px solid #93c5fd'
                }}>
                  <h4 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#2563eb',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <DollarSign size={24} />
                    Cost Savings
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[
                      { item: 'Billing staff reduction', amount: '$280,000' },
                      { item: 'Technology consolidation', amount: '$120,000' },
                      { item: 'Training & turnover reduction', amount: '$100,000' },
                      { item: 'Compliance & audit costs', amount: '$80,000' },
                      { item: 'Office space & overhead', amount: '$60,000' },
                      { item: 'Less: RCM service fee (6%)', amount: '($480,000)' }
                    ].map((row, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '12px',
                        backgroundColor: '#ffffff',
                        borderRadius: '6px',
                        fontSize: '0.9375rem'
                      }}>
                        <span style={{ color: '#374151' }}>{row.item}</span>
                        <span style={{
                          fontWeight: '600',
                          color: row.amount.includes('(') ? '#dc2626' : '#2563eb'
                        }}>{row.amount}</span>
                      </div>
                    ))}
                    <div style={{
                      marginTop: '12px',
                      paddingTop: '12px',
                      borderTop: '2px solid #93c5fd',
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '1.125rem',
                      fontWeight: '700'
                    }}>
                      <span style={{ color: '#2563eb' }}>Net Cost Savings:</span>
                      <span style={{ color: '#2563eb' }}>$160,000</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Impact */}
              <div style={{
                backgroundColor: '#4f46e5',
                color: '#ffffff',
                padding: '32px',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.125rem', marginBottom: '12px', opacity: 0.95 }}>
                  🎉 Total Annual Financial Benefit
                </div>
                <div style={{ fontSize: isMobile ? '2.5rem' : '3.5rem', fontWeight: 'bold', marginBottom: '8px' }}>
                  $1,200,000
                </div>
                <div style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '16px' }}>
                  15% increase in net practice income
                </div>
                <div style={{
                  marginTop: '24px',
                  padding: '16px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}>
                  Plus: Unified operations, better analytics, reduced complexity, improved compliance
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Specialty Coverage */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Specialties We Support
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: '16px'
            }}>
              {[
                'Internal Medicine', 'Family Practice', 'Pediatrics', 'OB/GYN',
                'Cardiology', 'Orthopedics', 'Dermatology', 'Neurology',
                'Gastroenterology', 'Pulmonology', 'Endocrinology', 'Rheumatology',
                'Psychiatry', 'Pain Management', 'Physical Medicine', 'Ophthalmology',
                'ENT', 'Urology', 'Nephrology', 'Oncology',
                'Allergy/Immunology', 'Infectious Disease', 'Geriatrics', 'Sports Medicine'
              ].map((specialty, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#eef2ff',
                  padding: '16px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '2px solid #c7d2fe',
                  fontSize: '0.9375rem',
                  fontWeight: '500',
                  color: '#4f46e5',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#4f46e5';
                  e.currentTarget.style.color = '#ffffff';
                  e.currentTarget.style.borderColor = '#4f46e5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#eef2ff';
                  e.currentTarget.style.color = '#4f46e5';
                  e.currentTarget.style.borderColor = '#c7d2fe';
                }}
                >
                  {specialty}
                </div>
              ))}
            </div>

            <div style={{
              marginTop: '32px',
              textAlign: 'center',
              padding: '24px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px'
            }}>
              <p style={{
                fontSize: '1rem',
                color: '#6b7280',
                marginBottom: '12px'
              }}>
                Don't see your specialty? We support 100+ specialties including surgical specialties, 
                diagnostic services, and ancillary services.
              </p>
              <Link to="/free-trial" style={{
                display: 'inline-block',
                color: '#4f46e5',
                fontWeight: '600',
                textDecoration: 'none'
              }}>
                Contact us about your specialty →
              </Link>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#eef2ff' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '48px',
              color: '#111827'
            }}>
              Common Questions from Multispecialty Practices
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                {
                  q: 'How do you handle multiple EHR/PM systems?',
                  a: 'We integrate with all major systems and can work with multiple EHRs simultaneously within one practice. Our platform aggregates data across systems, providing unified reporting and workflows regardless of your underlying technology infrastructure.'
                },
                {
                  q: 'Can we keep specialty-specific reporting while getting practice-wide visibility?',
                  a: 'Absolutely. Our dashboard provides both consolidated practice-wide metrics and drill-down specialty-specific reporting. Each specialty can see their performance while leadership gets the big picture. Custom reports available for any view you need.'
                },
                {
                  q: 'How do you ensure coding accuracy across diverse specialties?',
                  a: 'We assign specialty-specific coding teams—your cardiology claims are coded by cardiology-certified coders, orthopedics by orthopedic specialists, etc. Combined with automated coding validation and continuous specialty-specific training, we maintain 98%+ accuracy across all specialties.'
                },
                {
                  q: 'What if we add new specialties or locations?',
                  a: 'Our platform scales seamlessly. Adding specialties or locations doesn\'t disrupt existing operations. We handle onboarding, staff training, system integration, and ensure consistency across the expanded practice—typically completed within 2-3 weeks per new specialty/location.'
                },
                {
                  q: 'How quickly will we see ROI?',
                  a: 'Most multispecialty practices see measurable improvements within 90 days: better coding accuracy, faster collections, reduced denials. Full ROI typically realized within 6-9 months. Larger practices ($5M+) often see ROI within 4-6 months due to economies of scale.'
                },
                {
                  q: 'Do you support value-based care and MIPS reporting?',
                  a: 'Yes. We provide comprehensive support for value-based contracts, ACO participation, MIPS reporting, and quality measure tracking. Our platform aggregates data across specialties for population health management and helps optimize your value-based revenue.'
                }
              ].map((faq, idx) => (
                <div key={idx} style={{
                  backgroundColor: '#ffffff',
                  padding: '24px',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    marginBottom: '12px',
                    color: '#4f46e5',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px'
                  }}>
                    <Star size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{faq.q}</span>
                  </h3>
                  <p style={{
                    fontSize: '1rem',
                    lineHeight: '1.75',
                    color: '#374151',
                    margin: 0,
                    paddingLeft: '28px'
                  }}>
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
          color: '#ffffff'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{
              fontSize: isMobile ? '1.875rem' : '2.25rem',
              fontWeight: 'bold',
              marginBottom: '16px'
            }}>
              Ready to Transform Your Multispecialty Practice?
            </h2>
            <p style={{
              fontSize: '1.125rem',
              marginBottom: '32px',
              opacity: 0.95
            }}>
              Join leading multispecialty practices that increased revenue by $1M+ with integrated RCM
            </p>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '16px',
              justifyContent: 'center',
              marginBottom: '32px'
            }}>
              <Link to="/free-trial" style={{
                textDecoration: 'none',
                backgroundColor: '#F26522',
                color: '#ffffff',
                padding: '16px 32px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem',
                display: 'inline-block'
              }}>
                Get Enterprise Assessment →
              </Link>
              
              <a href="tel:+15103566069" style={{
                textDecoration: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: '#ffffff',
                padding: '16px 32px',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '1.125rem',
                display: 'inline-block',
                border: '2px solid rgba(255,255,255,0.3)'
              }}>
                📞 +1 (510) 356-6069
              </a>
            </div>

            <p style={{
              fontSize: '0.9375rem',
              opacity: 0.9
            }}>
              ⭐ 25-35% revenue increase • 30-40% cost reduction • Enterprise-grade platform
            </p>
          </div>
        </section>

        {/* Related Services */}
        <section style={{ padding: isMobile ? '40px 20px' : '60px 40px', backgroundColor: '#ffffff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              textAlign: 'center',
              marginBottom: '32px',
              color: '#111827'
            }}>
              Related Solutions
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: '20px'
            }}>
              {[
                { title: 'Independent Physician RCM', link: '/revenue-cycle-management-independent-physicians' },
                { title: 'Denial Management', link: '/denial-management-services-medical-clinics' },
                { title: 'View Pricing Options', link: '/pricing' }
              ].map((link, idx) => (
                <Link
                  key={idx}
                  to={link.link}
                  style={{
                    padding: '20px',
                    backgroundColor: '#eef2ff',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    border: '2px solid #c7d2fe',
                    transition: 'all 0.3s ease',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#4f46e5';
                    e.currentTarget.style.backgroundColor = '#e0e7ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#c7d2fe';
                    e.currentTarget.style.backgroundColor = '#eef2ff';
                  }}
                >
                  <span style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#4f46e5'
                  }}>
                    {link.title} →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

      </main>
    </>
  );
};

export default RCMMultispecialtyPractices;